import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Inbox as InboxIcon, Loader2, Phone, RefreshCw, Send } from 'lucide-react';
import { renderTemplate } from '../components/deal-detail/DealTextsTab';

// Inbox (Josh 10/12): every SMS thread, newest first, with unread counts — replies land here even
// when nobody is on the deal or the case. Same thread view + composer as Collections.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const segmentsOf = (t) => { const n = (t || '').length; const gsm = [...(t || '')].every(c => c.charCodeAt(0) < 128); const a = gsm ? 160 : 70, b = gsm ? 153 : 67; return n <= a ? 1 : Math.ceil(n / b); };
const threadTitle = (t) => t.company_name || t.contact_name || t.their_number;

export default function Inbox() {
  const { getAuthHeaders } = useAuth();
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [configured, setConfigured] = useState(null);   // null = not known yet (no banner until the API answers)
  const [loading, setLoading] = useState(true);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [selectedId, setSelectedId] = useState(params.get('thread') || null);
  const [view, setView] = useState({ thread: null, messages: [] });
  const [templates, setTemplates] = useState([]);
  const [draft, setDraft] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const headers = useMemo(() => ({ headers: getAuthHeaders() }), [getAuthHeaders]);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/collections/inbox${onlyUnread ? '?unread=true' : ''}`, headers);
      setThreads(r.data.threads || []); setConfigured(!!r.data.aircall_configured);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load inbox'); }
    finally { setLoading(false); }
  }, [onlyUnread, headers]);

  const loadThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await axios.get(`${API}/collections/threads/${id}`, headers);
      setView(r.data);
      await axios.post(`${API}/collections/threads/${id}/seen`, {}, headers);
      setThreads(prev => prev.map(t => (t.id === id ? { ...t, unread_count: 0 } : t)));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load conversation'); }
  }, [headers]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    axios.get(`${API}/collections/templates`, headers).then(r => setTemplates((r.data.templates || []).filter(t => t.active !== false))).catch(() => {});
  }, [headers]);
  useEffect(() => { if (selectedId) loadThread(selectedId); }, [selectedId, loadThread]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [view.messages]);
  useEffect(() => {
    const t = setInterval(() => { loadThreads(); if (selectedId) loadThread(selectedId); }, 20000);
    return () => clearInterval(t);
  }, [selectedId, loadThreads, loadThread]);

  const pick = (id) => { setSelectedId(id); setParams(id ? { thread: id } : {}); setDraft(''); setTemplateId(''); };
  const sel = view.thread;

  const applyTemplate = (tid) => {
    setTemplateId(tid);
    const t = templates.find(x => x.id === tid);
    if (t) setDraft(renderTemplate(t.body, {}));   // the server fills placeholders from the thread's deal on send
  };

  const send = async () => {
    if (!sel || !draft.trim()) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/collections/threads/${sel.id}/messages`, { body: draft, template_id: templateId || null }, headers);
      toast.success(r.data.dry_run ? 'Stored as DRY RUN — Aircall is not configured' : 'Text sent');
      setDraft(''); setTemplateId('');
      await loadThread(sel.id); await loadThreads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><InboxIcon className="h-6 w-6" /> Inbox</h1>
            <p className="text-sm text-muted-foreground">Every text conversation, newest first. Replies land here even when nobody is on the deal.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant={onlyUnread ? 'default' : 'outline'} size="sm" onClick={() => setOnlyUnread(v => !v)}>Unread only</Button>
            <Button variant="outline" size="icon" onClick={loadThreads} title="Refresh"><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-1">
            <CardHeader className="py-3"><CardTitle className="text-base">Conversations ({threads.length})</CardTitle></CardHeader>
            <CardContent className="p-0 max-h-[70vh] overflow-y-auto">
              {loading ? <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                : threads.length === 0 ? <div className="p-6 text-sm text-muted-foreground text-center">No conversations yet.</div>
                : threads.map(t => (
                  <button key={t.id} onClick={() => pick(t.id)}
                    className={`w-full text-left px-4 py-3 border-b hover:bg-slate-50 ${t.id === selectedId ? 'bg-blue-50' : ''}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className={`truncate ${t.unread_count > 0 ? 'font-semibold' : 'font-medium'}`}>{threadTitle(t)}</div>
                      <div className="flex items-center gap-1 shrink-0">
                        {t.unread_count > 0 && <Badge className="bg-red-600 text-white">{t.unread_count}</Badge>}
                        {t.opted_out && <Badge variant="outline" className="text-red-700 border-red-300">STOP</Badge>}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {t.last_direction === 'in' ? '← ' : '→ '}{t.last_body || ''}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex justify-between">
                      <span>{(t.deals || []).map(d => `#${d.deal_id_number}`).join(', ') || t.their_number}</span>
                      <span>{fmtWhen(t.last_message_at)}</span>
                    </div>
                  </button>
                ))}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            {!sel ? (
              <CardContent className="p-10 text-center text-muted-foreground">Pick a conversation.</CardContent>
            ) : (
              <>
                <CardHeader className="py-3 border-b">
                  <CardTitle className="text-base">{threadTitle(sel)}</CardTitle>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                    <span key={`phone-${sel.id}`} className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{sel.their_number}</span>
                    {(sel.deals || []).map(d => (
                      <Link key={d.id} to={`/deals/${d.id}`} className="text-blue-700 hover:underline">#{d.deal_id_number} {d.deal_name} · {d.status}</Link>
                    ))}
                    {sel.case_id && <Link to={`/collections?case=${sel.case_id}`} className="text-blue-700 hover:underline">Collections case</Link>}
                    {!(sel.deals || []).length && <span className="text-amber-700">no contact matched this number</span>}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="h-[46vh] overflow-y-auto p-4 space-y-2 bg-slate-50">
                    {view.messages.map(m => (
                      <div key={m.id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${m.direction === 'out' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>
                          <div className="whitespace-pre-wrap">{m.body}</div>
                          <div className={`text-[10px] mt-1 ${m.direction === 'out' ? 'text-blue-100' : 'text-muted-foreground'}`}>
                            {fmtWhen(m.created_at)}{m.direction === 'out' ? ` · ${m.sent_by_email || 'sent'} · ${m.status}` : ' · merchant'}{m.error && <span className="text-red-200"> · {m.error}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={bottomRef} />
                  </div>
                  <div className="border-t p-3 space-y-2">
                    {sel.opted_out && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">This number replied STOP. Texting is blocked.</div>}
                    <div className="flex gap-2 items-center">
                      <Select value={templateId} onValueChange={applyTemplate}>
                        <SelectTrigger className="w-72 h-9"><SelectValue placeholder="Template…" /></SelectTrigger>
                        <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <span className="text-xs text-muted-foreground ml-auto">{draft.length} chars · {segmentsOf(draft)} segment{segmentsOf(draft) === 1 ? '' : 's'}</span>
                    </div>
                    <Textarea rows={3} value={draft} onChange={e => setDraft(e.target.value)} placeholder="Type a reply…" disabled={sel.opted_out} />
                    <div className="flex justify-end">
                      <Button onClick={send} disabled={sending || sel.opted_out || !draft.trim()}>
                        {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}{configured === false ? 'Send (dry run)' : 'Send'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}
